import { Badge, FlexRow, LinkButton, MainMenuAvatar, Panel } from '@epam/promo';
import { MainMenuCustomElement } from '@epam/uui-components';
import { useLocation } from 'react-router-dom';

import {
  useToast,
  useTaskSwitcher,
  useAdminLogout,
  useRecognitionSwitcher,
  useBroadcastSwitcher,
} from 'hooks';
import { useLeaveTaskGroupsModal } from 'hooks/context/use-leave-task-groups-modal';
import { useApplicationStore } from 'store/application';
import { taskRoutes } from 'router/taskRoutes';
import { broadcastRoutes } from 'router/broadcastRoutes';
import { USER_ROLE } from 'models/user.model';
import { NavProfileProps } from './NavProfile.model';
import css from './NavProfile.module.scss';
import { useMemo } from 'react';
import { taskManagementRoutes } from 'router/taskManagementRoutes';
import { recognitionRoutes } from 'router/recognitionRoutes';

export const NavProfile = ({ avatar, role, isRevoked }: NavProfileProps) => {
  const toast = useToast();
  const switchTask = useTaskSwitcher();
  const switchRecognition = useRecognitionSwitcher();
  const switchBroadcast = useBroadcastSwitcher();
  const { mutate: logout } = useAdminLogout();
  const location = useLocation();
  const { isLoading } = useApplicationStore((state) => state);
  const leaveTaskGroups = useLeaveTaskGroupsModal();

  const logoutCurrentUser = () => {
    if (isLoading) return null;

    const logoutToast = () => {
      toast.warning(
        `Checking unsaved changes...
        You will be redirected in 3 seconds`,
      );

      setTimeout(logout, 3000);
    };

    if (location.pathname.includes(taskManagementRoutes.taskGroups)) {
      return leaveTaskGroups({
        onSuccess: logoutToast,
        onAbort: logout,
      });
    }

    if (location.pathname.includes(taskRoutes.base)) {
      return switchTask({
        onSuccess: logoutToast,
        onAbort: logout,
      });
    }

    if (location.pathname.includes(broadcastRoutes.base)) {
      return switchBroadcast({
        onSuccess: logoutToast,
        onAbort: logout,
      });
    }

    if (location.pathname === recognitionRoutes.base) {
      return switchRecognition({
        onSuccess: logoutToast,
        onAbort: logout,
      });
    }

    return logoutToast();
  };

  const shownRole = useMemo(() => {
    if (isRevoked || !(role in USER_ROLE)) return USER_ROLE.GUEST;

    return USER_ROLE[role];
  }, [isRevoked, role]);

  return (
    <MainMenuCustomElement estimatedWidth={210} priority={10}>
      <FlexRow
        alignItems="center"
        columnGap="12"
        cx={css.profile}
        justifyContent="end">
        <LinkButton
          caption="Log out"
          cx={{
            [css.badge__btn]: true,
            [css['badge__btn-disabled']]: isLoading,
          }}
          size="36"
          onClick={logoutCurrentUser}
        />
        <Panel cx={css.badge}>
          <MainMenuAvatar avatarUrl={avatar} cx={css.badge__avatar} />
          <Badge
            caption={shownRole}
            color="blue"
            cx={css.badge__role}
            fill="solid"
            size="18"
          />
        </Panel>
      </FlexRow>
    </MainMenuCustomElement>
  );
};
